import { useSignal } from "@preact/signals-react";
import { useState, useMemo } from "react";
import { toast } from "react-toastify";
import { FaSearch, FaChevronDown } from "react-icons/fa";
import { currencyWithPrefix } from "../../../utils/currencyUtils";
import { formatDateToBR } from "../../../utils/DateUtils";
import { formatDocNumber } from "../../../utils/documentUtils";
import DateFilter from "../../molecules/DateFilter";
import PageHeader from "../../molecules/PageHeader";
import Loading from "../../ions/Loading";
import { OperationsSummaryModal } from "../../molecules/OperationsSummaryModal";
import ToggleSwitch from "../../atoms/ToogleSwitch";
import appConfig from "../../../config/app-config";

interface Lote {
    date_mov: string;
    batch_seqno: string;
    contract_number: string;
    num_adlib: number;
    our_number: string;
    doc_sacado: string;
    debt_expire_date: string;
    nominal_value: number;
    principal_value: number;
    fee_value: number;
    permanence_value: number;
    discount_value: number;
    paid_value: number;
    liquidated_value: number;
    liquidation_type: string;
    modality: string;
    liquidation_bank: string;
    username: string;
    batch_status: string;
    effectived_on: string | null;
}

const { address: API_URL, key: API_KEY } = appConfig.apis.dimensaChupimApiEnv;

const DimensaLoteList: React.FC = () => {
    const lotes = useSignal<Lote[]>([]);
    const isLoading = useSignal(false);
    const [dateField, setDateField] = useState(new Date().toISOString().split('T')[0]);
    const [statusField, setStatusField] = useState('');
    const [currentSearchStatus, setCurrentSearchStatus] = useState('');
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [showInactive, setShowInactive] = useState(true);

    const fetchLotes = async () => {
        if (!dateField || !statusField) {
            toast.error("Selecione a data e o status do lote");
            return;
        }

        isLoading.value = true;
        try {
            const response = await fetch(`${API_URL}/backoffice/listarLote`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    date_field: dateField,
                    status_field: statusField
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error Response:', errorText);
                throw new Error(`Erro: ${response.status}`);
            }

            const data = await response.json();
            console.log('API Response:', data);

            if (data.success && data.content) {
                lotes.value = data.content;
                setCurrentSearchStatus(statusField);
                toast.success(`${lotes.value.length} lote(s) encontrado(s)`);
            } else {
                lotes.value = [];
                setCurrentSearchStatus(statusField);
                toast.info("Nenhum lote encontrado");
            }
        } catch (error: any) {
            console.error('Fetch Error:', error);
            toast.error(`Erro ao buscar lotes: ${error.message}`);
            lotes.value = [];
        } finally {
            isLoading.value = false;
        }
    };

    const formatCurrency = (value: number) => currencyWithPrefix(value || 0);

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        // Se já tem hora, usa direto; senão adiciona T00:00:00
        const dateToFormat = dateString.includes('T') ? dateString : dateString + 'T00:00:00';
        return formatDateToBR(dateToFormat);
    };

    const formatModality = (modality: string) => {
        const map: Record<string, string> = {
            'EMPR PJ': 'Empréstimo PJ',
            'DESSCOOB': 'Sessão sem Regresso',
            'EM03F365': 'CPRF Varejo'
        };
        return map[modality] || modality || '-';
    };

    const isInactive = (lote: Lote): boolean => {
        if (lote.batch_status === 'ESTORNADO') return true;
        if (currentSearchStatus === 'EF' && !lote.effectived_on) return true;
        return false;
    };

    const getDisplayStatus = (lote: Lote): string => {
        if (lote.batch_status === 'ESTORNADO') return lote.batch_status;
        if (currentSearchStatus === 'EF' && !lote.effectived_on) return 'NÃO EFETUADO';
        return lote.batch_status || 'ATIVO';
    };

    const summary = useMemo(() => {
        console.log('Calculando summary, lotes:', lotes.value);
        const activeLotes = lotes.value.filter(lote => !isInactive(lote));

        const total = activeLotes.reduce((sum, lote) => {
            const value = Number(lote.paid_value) || 0;
            return sum + value;
        }, 0);

        const bankTotals = activeLotes.reduce((acc, lote) => {
            const bank = lote.liquidation_bank || 'Não informado';
            if (!acc[bank]) acc[bank] = 0;
            acc[bank] += Number(lote.paid_value) || 0;
            return acc;
        }, {} as Record<string, number>);

        console.log('Summary calculado:', { total, bankTotals });
        return {
            total,
            bankTotals: Object.entries(bankTotals).map(([bank, total]) => ({ bank, total }))
        };
    }, [lotes.value, currentSearchStatus]);

    const filteredLotes = useMemo(() => {
        if (showInactive) return lotes.value;
        return lotes.value.filter(lote => !isInactive(lote));
    }, [lotes.value, showInactive, currentSearchStatus]);

    const groupPaidSums = useMemo(() => {
        const map = new Map<string, number>();
        if (currentSearchStatus !== 'FE') return map;
        lotes.value.forEach(lote => {
            if (!lote.our_number) return;
            const current = map.get(lote.our_number) || 0;
            map.set(lote.our_number, current + (Number(lote.paid_value) || 0));
        });
        return map;
    }, [lotes.value, currentSearchStatus]);

    const isGroupExceedingNominal = (lote: Lote): boolean => {
        if (currentSearchStatus !== 'FE') return false;
        if (!lote.our_number) return false;
        const groupSum = groupPaidSums.get(lote.our_number) || 0;
        const liquidated = Number(lote.liquidated_value) || 0;
        const nominal = Number(lote.nominal_value) || 0;
        return (groupSum + liquidated) > nominal;
    };

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            <div className="grid grid-cols-1">
                {isLoading.value && <Loading />}

                <PageHeader
                    title="Lotes de Liquidação DIMENSA"
                    apiVersion=""
                    breadcrumb={[
                        { label: 'Financeiro', url: '#' },
                        { label: 'Lotes DIMENSA', url: '#' }
                    ]}
                >
                    <div className="pt-4"></div>
                </PageHeader>

                <div className={`bg-gray-50 dark:bg-slate-700 p-4 rounded-lg mb-4 ${isLoading.value ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div className="flex items-center gap-4 justify-between">
                        <div className="flex items-center gap-4">
                            <DateFilter
                                label="Data Movimento:"
                                value={dateField}
                                onChange={setDateField}
                            />
                            <div className="flex items-center gap-2 relative">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Status do Lote:
                                </label>
                                <div className="relative">
                                    <select
                                        value={statusField}
                                        onChange={(e) => setStatusField(e.target.value)}
                                        className="px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer"
                                        style={{
                                            backgroundImage: 'none',
                                            WebkitAppearance: 'none',
                                            MozAppearance: 'none'
                                        }}
                                    >
                                        <option value="">Selecione...</option>
                                        <option value="FE">Lançado</option>
                                        <option value="EF">Efetivado</option>
                                    </select>
                                    <FaChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none text-xs" />
                                </div>
                            </div>
                            <button
                                onClick={fetchLotes}
                                disabled={isLoading.value}
                                className="text-white py-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <FaSearch />
                                Consultar Lotes
                            </button>
                            {lotes.value.length > 0 && (
                                <>
                                    <span className="px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded font-medium text-sm">
                                        Total: {filteredLotes.length} lote(s)
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Exibir Não Ativos
                                        </label>
                                        <ToggleSwitch
                                            checked={showInactive}
                                            onChange={setShowInactive}
                                            activeClass="bg-blue-600"
                                            inactiveClass="bg-gray-300 dark:bg-gray-600"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        {lotes.value.length > 0 && (
                            <button
                                onClick={() => setShowSummaryModal(true)}
                                className="text-white py-2 px-4 rounded-lg bg-yellow-600 hover:bg-yellow-700 flex items-center gap-2"
                            >
                                Resumo Operações
                            </button>
                        )}
                    </div>
                </div>

                {lotes.value.length === 0 ? (
                    <div className="text-center py-20 text-gray-500 dark:text-gray-400 italic">
                        Selecione a data, status e clique em Consultar para visualizar lotes
                    </div>
                ) : (
                    <div className={`max-h-[calc(100vh-200px)] overflow-x-auto bg-white dark:bg-slate-800 shadow border border-gray-200 dark:border-slate-600 ${isLoading.value ? 'opacity-50 pointer-events-none' : ''}`}>
                        <table className="min-w-full text-sm table-auto auto-cols-auto">
                            <thead className="text-xs text-slate-500 uppercase bg-gray-50 sticky top-0 shadow-md dark:bg-slate-800 dark:text-gray-50 z-50">
                                <tr>
                                    <th className="px-2 py-2 text-left border-b">Lote</th>
                                    <th className="px-2 py-2 text-left border-b">Modalidade</th>
                                    <th className="px-2 py-2 text-left border-b">Contrato</th>
                                    <th className="px-2 py-2 text-left border-b">Nosso Nº</th>
                                    <th className="px-2 py-2 text-center border-b">Data Venc.</th>
                                    <th className="px-2 py-2 text-right border-b">V. Nominal</th>
                                    <th className="px-2 py-2 text-right border-b">Principal</th>
                                    <th className="px-2 py-2 text-right border-b">Juros</th>
                                    <th className="px-2 py-2 text-right border-b">Permanência</th>
                                    <th className="px-2 py-2 text-right border-b">Desconto</th>
                                    <th className="px-2 py-2 text-right border-b">Valor Pago</th>
                                    <th className="px-2 py-2 text-right border-b">V. Liquidado</th>
                                    <th className="px-2 py-2 text-left border-b">Tipo Liq.</th>
                                    <th className="px-2 py-2 text-left border-b">Banco</th>
                                    <th className="px-2 py-2 text-left border-b">Sacado</th>
                                    <th className="px-2 py-2 text-left border-b">Usuário</th>
                                    <th className="px-2 py-2 text-center border-b">Status</th>
                                </tr>
                            </thead>
                            <tbody className="overflow-y-auto">
                                {filteredLotes.map((lote, index) => {
                                    const highlightGroup = isGroupExceedingNominal(lote);
                                    const highlightClass = highlightGroup ? 'bg-yellow-100 dark:bg-yellow-900/40' : '';
                                    const inactive = isInactive(lote);
                                    return (
                                        <tr
                                            key={index}
                                            className="border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
                                        >
                                            <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.batch_seqno || '-'}</td>
                                            <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{formatModality(lote.modality)}</td>
                                            <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{`${lote.contract_number || ''}-${lote.num_adlib ?? ''}`}</td>
                                            <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.our_number || '-'}</td>
                                            <td className="px-2 py-2 text-center text-gray-900 dark:text-gray-100">{formatDate(lote.debt_expire_date)}</td>
                                            <td className={`px-2 py-2 text-right text-gray-900 dark:text-gray-100 ${highlightClass}`}>{formatCurrency(lote.nominal_value)}</td>
                                            <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.principal_value)}</td>
                                            <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.fee_value)}</td>
                                            <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.permanence_value)}</td>
                                            <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.discount_value)}</td>
                                            <td className={`px-2 py-2 text-right text-gray-900 dark:text-gray-100 ${highlightClass}`}>{formatCurrency(lote.paid_value)}</td>
                                            <td className={`px-2 py-2 text-right text-gray-900 dark:text-gray-100 ${highlightClass}`}>{formatCurrency(lote.liquidated_value)}</td>
                                            <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.liquidation_type || '-'}</td>
                                            <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.liquidation_bank || '-'}</td>
                                            <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{formatDocNumber(lote.doc_sacado)}</td>
                                            <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.username || '-'}</td>
                                            <td className="px-2 py-2 text-center">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${inactive
                                                    ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                                    : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                                    }`}>
                                                    {getDisplayStatus(lote)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <OperationsSummaryModal
                isOpen={showSummaryModal}
                onClose={() => setShowSummaryModal(false)}
                totalLiquidated={summary.total}
                bankTotals={summary.bankTotals}
            />
        </div>
    );
};

export default DimensaLoteList;